// Payment Manager for Stripe integration
export class PaymentManager {
  static initialize() {
    this.stripe = null;
    
    // Check if we're in a browser environment
    const isBrowser = typeof window !== 'undefined';
    const isProduction = typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production';
    
    this.publishableKey = (typeof process !== 'undefined' && process.env && process.env.STRIPE_PUBLISHABLE_KEY) || '';
    this.secretKey = (typeof process !== 'undefined' && process.env && process.env.STRIPE_SECRET_KEY) || '';
    this.webhookSecret = (typeof process !== 'undefined' && process.env && process.env.STRIPE_WEBHOOK_SECRET) || '';
    
    // Initialize Stripe in production (always attach .catch — Stripe may reject async with
    // "invalid origin" if the domain isn’t registered for this publishable key in Dashboard)
    if (this.publishableKey && isBrowser) {
      void this.loadStripe().catch(() => {
        this.stripe = null;
      });
    }
    
    // PaymentManager initialized
  }

  // Load Stripe.js
  static async loadStripe() {
    if (typeof window === 'undefined') return;
    
    try {
      // Use script tag loading instead of dynamic import for webpack compatibility
      if (!window.Stripe) {
        await this.loadStripeScript();
      }
      
      this.stripe = window.Stripe(this.publishableKey);
      return this.stripe;
    } catch (error) {
      // Failed to load Stripe - handled silently
      return null;
    }
  }

  // Load Stripe script
  static async loadStripeScript() {
    return new Promise((resolve, reject) => {
      if (window.Stripe) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Stripe script'));
      document.head.appendChild(script);
    });
  }

  // Create payment intent
  static async createPaymentIntent(amount, currency = 'AUD', metadata = {}) {
    try {
      const response = await fetch('/api/payments/create-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({
          amount: Math.round(amount * 100), // Convert to cents
          currency,
          metadata
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create payment intent');
      }

      const result = await response.json();
      
      // Track payment intent creation
      if (window.AnalyticsManager) {
        window.AnalyticsManager.trackEvent('payment_intent_created', {
          amount,
          currency,
          intent_id: result.client_secret
        });
      }

      return result;
    } catch (error) {
      // Payment intent creation error - handled silently
      throw error;
    }
  }

  // Process payment with Stripe Elements
  static async processPayment(paymentIntent, paymentElement) {
    if (!this.stripe) {
      throw new Error('Stripe not initialized');
    }

    try {
      const { error, paymentIntent: confirmedPayment } = await this.stripe.confirmPayment({
        elements: paymentElement,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success`,
        },
        redirect: 'if_required'
      });

      if (error) {
        throw new Error(error.message);
      }

      // Track successful payment
      if (window.AnalyticsManager) {
        window.AnalyticsManager.trackEvent('payment_successful', {
          amount: confirmedPayment.amount,
          currency: confirmedPayment.currency,
          payment_intent_id: confirmedPayment.id
        });
      }

      return confirmedPayment;
    } catch (error) {
      // Payment processing error - handled silently
      
      // Track payment failure
      if (window.AnalyticsManager) {
        window.AnalyticsManager.trackEvent('payment_failed', {
          error: error.message
        });
      }
      
      throw error;
    }
  }

  // Create subscription
  static async createSubscription(priceId, customerId = null) {
    try {
      const response = await fetch('/api/subscriptions/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({
          priceId,
          customerId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create subscription');
      }

      const result = await response.json();
      
      // Track subscription creation
      if (window.AnalyticsManager) {
        window.AnalyticsManager.trackEvent('subscription_created', {
          price_id: priceId,
          subscription_id: result.subscription.id
        });
      }

      return result;
    } catch (error) {
      // Subscription creation error - handled silently
      throw error;
    }
  }

  // Cancel subscription
  static async cancelSubscription(subscriptionId, cancelAtPeriodEnd = true) {
    try {
      const response = await fetch('/api/subscriptions/cancel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({
          subscriptionId,
          cancelAtPeriodEnd
        })
      });

      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }

      const result = await response.json();
      
      // Track subscription cancellation
      if (window.AnalyticsManager) {
        window.AnalyticsManager.trackEvent('subscription_cancelled', {
          subscription_id: subscriptionId,
          cancel_at_period_end: cancelAtPeriodEnd
        });
      }

      return result;
    } catch (error) {
      // Subscription cancellation error - handled silently
      throw error;
    }
  }

  // Get customer payment methods
  static async getPaymentMethods(customerId) {
    try {
      const response = await fetch(`/api/payments/methods/${customerId}`, {
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get payment methods');
      }

      const result = await response.json();
      return result.paymentMethods;
    } catch (error) {
      // Get payment methods error - handled silently
      throw error;
    }
  }

  // Update payment method
  static async updatePaymentMethod(paymentMethodId, paymentElement) {
    if (!this.stripe) {
      throw new Error('Stripe not initialized');
    }

    try {
      const { error, paymentMethod } = await this.stripe.updatePaymentMethod({
        paymentMethodId,
        elements: paymentElement
      });

      if (error) {
        throw new Error(error.message);
      }

      return paymentMethod;
    } catch (error) {
      // Update payment method error - handled silently
      throw error;
    }
  }

  // Get subscription details
  static async getSubscription(subscriptionId) {
    try {
      const response = await fetch(`/api/subscriptions/${subscriptionId}`, {
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get subscription');
      }

      const result = await response.json();
      return result.subscription;
    } catch (error) {
      // Get subscription error - handled silently
      throw error;
    }
  }

  // Get customer invoices
  static async getInvoices(customerId, limit = 10) {
    try {
      const response = await fetch(`/api/payments/invoices/${customerId}?limit=${limit}`, {
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get invoices');
      }

      const result = await response.json();
      return result.invoices;
    } catch (error) {
      // Get invoices error - handled silently
      throw error;
    }
  }

  // Handle webhook events
  static async handleWebhookEvent(event) {
    try {
      // Verify webhook signature in production
      const isProd = typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production';
      if (isProd && !this.verifyWebhookSignature(event)) {
        throw new Error('Invalid webhook signature');
      }

      const { type, data } = event;

      switch (type) {
        case 'payment_intent.succeeded':
          await this.handlePaymentSuccess(data.object);
          break;
        case 'payment_intent.payment_failed':
          await this.handlePaymentFailure(data.object);
          break;
        case 'customer.subscription.created':
          await this.handleSubscriptionCreated(data.object);
          break;
        case 'customer.subscription.updated':
          await this.handleSubscriptionUpdated(data.object);
          break;
        case 'customer.subscription.deleted':
          await this.handleSubscriptionDeleted(data.object);
          break;
        case 'invoice.payment_succeeded':
          await this.handleInvoicePaymentSucceeded(data.object);
          break;
        case 'invoice.payment_failed':
          await this.handleInvoicePaymentFailed(data.object);
          break;
        default:
          // Unhandled webhook event type - handled silently
      }
    } catch (error) {
      // Webhook handling error - handled silently
      throw error;
    }
  }

  // Handle payment success
  static async handlePaymentSuccess(paymentIntent) {
    // Update user subscription status
    if (window.AnalyticsManager) {
      window.AnalyticsManager.trackEvent('webhook_payment_succeeded', {
        payment_intent_id: paymentIntent.id,
        amount: paymentIntent.amount
      });
    }
  }

  // Handle payment failure
  static async handlePaymentFailure(paymentIntent) {
    if (window.AnalyticsManager) {
      window.AnalyticsManager.trackEvent('webhook_payment_failed', {
        payment_intent_id: paymentIntent.id,
        error: paymentIntent.last_payment_error?.message
      });
    }
  }

  // Handle subscription events
  static async handleSubscriptionCreated(subscription) {
    // Subscription created - handled silently
  }

  static async handleSubscriptionUpdated(subscription) {
    // Subscription updated - handled silently
  }

  static async handleSubscriptionDeleted(subscription) {
    console.log('Subscription deleted:', subscription.id);
  }

  static async handleInvoicePaymentSucceeded(invoice) {
    console.log('Invoice payment succeeded:', invoice.id);
  }

  static async handleInvoicePaymentFailed(invoice) {
    console.log('Invoice payment failed:', invoice.id);
  }

  // Verify webhook signature
  static verifyWebhookSignature(event) {
    // Implementation would verify Stripe webhook signature
    // For now, return true in development
    const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development';
    return isDev;
  }

  // Get auth token
  static getAuthToken() {
    if (typeof window !== 'undefined' && window.AuthManager) {
      return window.AuthManager.getSessionToken();
    }
    return null;
  }

  // Format amount for display
  static formatAmount(amount, currency = 'AUD') {
    const formatter = new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    });
    return formatter.format(amount / 100);
  }

  // Get Stripe instance
  static getStripe() {
    return this.stripe;
  }
}